import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Plus } from 'lucide-react'
import { columns } from './columns'
import { DataTable } from './data-table'
import { useClinics } from '@/hooks/useClinics'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useViewMode } from '@/stores/viewMode'
import { PageHeader } from '@/components/PageHeader'

export default function ClinicsPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [search, setSearch] = useState('')
  const { mode, id: viewModeId } = useViewMode()

  // When in provider mode, pass providerId to filter clinics
  const providerId = mode === 'provider' && viewModeId ? viewModeId : undefined

  const { clinics, pagination, isLoading, isValidating, isError, mutate } = useClinics({
    page,
    pageSize,
    search,
    providerId,
  })

  // Reset page to 1 when search changes
  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, [])

  if (isError) {
    return (
      <div>
        <div className="text-center text-destructive">載入診所列表失敗。請稍後再試。</div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-[200px] mb-4" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  // Generate page numbers for pagination
  const generatePaginationItems = () => {
    if (!pagination) return []

    const items = []
    const { page: currentPage, totalPages } = pagination

    // Always show first page
    items.push(1)

    if (currentPage > 3) {
      items.push('ellipsis')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      items.push(i)
    }

    if (currentPage < totalPages - 2) {
      items.push('ellipsis')
    }

    // Always show last page
    if (totalPages > 1) {
      items.push(totalPages)
    }

    return items
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="診所管理"
        description="管理診所資訊和人員"
        level={mode === 'admin' ? 'page' : 'section'}
      >
        <Button
          onClick={() => {
            // 依當前 scope 決定 create URL，讓 ClinicInfo 能正確帶入 provider_id
            const createPath =
              mode === 'provider' && viewModeId
                ? `/provider/${viewModeId}/clinic/create`
                : '/admin/clinic/create'
            navigate(createPath)
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          新增診所
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="p-6">
          <DataTable
            columns={columns}
            data={clinics || []}
            onSearch={handleSearch}
            isLoading={isValidating}
            meta={{
              reloadData: () => mutate(),
            }}
          />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 1) setPage(page - 1)
                      }}
                    />
                  </PaginationItem>

                  {generatePaginationItems().map((item, index) => {
                    if (item === 'ellipsis') {
                      return (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )
                    }

                    return (
                      <PaginationItem key={item}>
                        <PaginationLink
                          href="#"
                          isActive={item === page}
                          onClick={(e) => {
                            e.preventDefault()
                            setPage(item as number)
                          }}
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  })}

                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (pagination && page < pagination.totalPages) setPage(page + 1)
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
